import {
  formatNumber,
  getLocaleNumberSymbol,
  NumberSymbol,
} from '@angular/common';

import { StaticMessage } from '@shared/enums';
import {
  IIdentity,
  CellDisplayConfig,
  CellEditConfig,
  FilterColumn,
  GridColumn,
  DialogCellConfig,
} from '@grid/models';
import { Constants as GridConstants } from '@grid/utils/constants';
import {
  CheckboxEditComponent,
  DeleteButtonComponent,
  TextDisplayComponent,
  TextEditComponent,
} from '@grid/components';
import {
  CommentDialogEditComponent,
  DecimalDisplayComponent,
} from '@shared/components';
import { ExtendedDataType } from '@shared/models';
import { GridAlignment } from '@grid/enums';
import { getStaticMessage } from '../localization';
import { Constants } from '../constants';
import { isNumericValue } from '../number';

export const GridColumnTemplates: { [key: string]: Partial<GridColumn<any>> } =
  {
    checkbox: {
      header: {
        alignment: GridAlignment.Center,
      },
      columnAlignment: GridAlignment.Center,
      display: { type: CheckboxEditComponent },
      width: 100,
      tabIndex: -1,
      allowSorting: false,
      tooltip: {
        onlyTextOverflow: false,
        hideOnClick: false,
      },
      disableFocusedBackground: true,
    },
  };

const commentColumn: GridColumn<any> = {
  columnDef: 'comment',
  header: {
    name: getStaticMessage(StaticMessage.CommentColumn),
  },
  display: {
    type: TextDisplayComponent,
    config: { options: { displayLength: 300 } },
  },
  allowSorting: false,
  bindClickEvent: true,
  edit: (_data: any, cell: HTMLElement): CellEditConfig<any> => {
    return cell?.clientWidth > 300
      ? { type: TextEditComponent, config: { options: { maxLength: 1000 } } }
      : {
          type: CommentDialogEditComponent,
          mode: 'dialog',
          config: {
            additionalData: {
              maxLength: Constants.FieldLimits.maxGridCommentLength,
            },
          },
        };
  },
  minWidth: 200,
};

function commentColumnFn<T extends IIdentity>(
  titleResolver: (data: T) => string,
  beforeOpenAdaptor?: (data: Partial<T>, additionalData?: any) => void,
  afterCloseAdaptor?: (data: Partial<T>, additionalData?: any) => void
): GridColumn<T> {
  return {
    ...commentColumn,
    edit: (_data: T, cell: HTMLElement): CellEditConfig<T> => {
      const edit = (
        commentColumn.edit as (data: T, cell: HTMLElement) => CellEditConfig<T>
      )(_data, cell);

      if (edit.mode === 'inline') {
        return edit;
      }

      return {
        ...edit,
        config: {
          ...edit.config,
          additionalData: {
            title: titleResolver?.(_data),
            ...(edit.config as DialogCellConfig<any>)?.additionalData,
          },
          beforeOpenAdaptor,
          afterCloseAdaptor,
        },
      };
    },
  };
}

const isActiveColumn: GridColumn<any> = {
  columnDef: 'isActive',
  ...GridColumnTemplates.checkbox,
  header: {
    ...GridColumnTemplates.checkbox.header,
    name: getStaticMessage(StaticMessage.ActiveColumn),
  },
  width: 80,
  tooltip: {
    ...GridColumnTemplates.checkbox.tooltip,
    disabled: true,
  },
};

const isMandatoryColumn: GridColumn<any> = {
  columnDef: 'isMandatory',
  ...GridColumnTemplates.checkbox,
  header: {
    ...GridColumnTemplates.checkbox.header,
    name: getStaticMessage(StaticMessage.MandatoryColumn),
  },
  tooltip: {
    ...GridColumnTemplates.checkbox.tooltip,
    disabled: true,
  },
};

const deleteColumn: GridColumn<any> = {
  columnDef: 'delete',
  header: {
    name: '',
  },
  display: { type: DeleteButtonComponent },
  allowSorting: false,
  allowEditing: false,
  width: 68,
  tabIndex: -1,
  tooltip: {
    onlyTextOverflow: false,
    hideOnClick: false,
    message: (context) => {
      const message = getStaticMessage(
        !context.allowDeleting
          ? StaticMessage.ViewModeTooltip
          : !context.allowDeletingRow
          ? StaticMessage.CannotDeleteRowTooltip
          : StaticMessage.DeleteRowTooltip
      );

      return message;
    },
  },
  disableFocusedBackground: true,
};

const actionsColumn: GridColumn<any> = {
  columnDef: 'actions',
  header: {
    name: '',
  },
  allowSorting: false,
  allowEditing: false,
  width: 78,
  tabIndex: -1,
  disableFocusedBackground: true,
};

/** Typed and worked-out values show the same way, with 2 decimals. */
export function getDecimalCellDisplay<T extends IIdentity>(
  dataTypeId: number
): CellDisplayConfig<T> {
  return {
    type: DecimalDisplayComponent,
    config: {
      options: {
        allowPercentage: dataTypeId === ExtendedDataType.Percentage,
      },
    },
  };
}

/** A filter matches a decimal cell as `getDecimalCellDisplay` shows it (e.g. a stored 5 is found by typing 5.00). */
export function getDecimalCellFilterValueFn(
  dataTypeId: number,
  locale: string
): FilterColumn['computeFilterDataValueFn'] {
  const suffix = dataTypeId === ExtendedDataType.Percentage ? '%' : '';
  const groupSeparator = getLocaleNumberSymbol(locale, NumberSymbol.Group);

  // The grid matches the filter as a substring of one string, so each accepted
  // form (shown, shown without grouping, stored) goes on its own line: the filter
  // input is single-line, which stops a typed value from matching across two forms.
  return (val) => {
    if (!isNumericValue(val)) {
      return val;
    }

    const shown = formatNumber(
      +val,
      locale,
      GridConstants.DefaultValues.decimalDisplayFormat
    );

    return [
      ...new Set([
        shown + suffix,
        shown.replaceAll(groupSeparator, '') + suffix,
        String(val),
      ]),
    ].join('\n');
  };
}

export const GridColumns = {
  comment: commentColumn,
  commentFn: commentColumnFn,
  isActive: isActiveColumn,
  isMandatory: isMandatoryColumn,
  delete: deleteColumn,
  actions: actionsColumn,
};
